const ACCOUNTS_STORAGE_KEY = "jk_accounts";
const ACTIVE_ACCOUNT_STORAGE_KEY = "jk_active_account_id";
const LEGACY_TOKEN_STORAGE_KEY = "token";
const LEGACY_USER_STORAGE_KEY = "user";

function canUseLocalStorage() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function canUseSessionStorage() {
  return typeof window !== "undefined" && typeof window.sessionStorage !== "undefined";
}

function getPreferredStorageRef() {
  return getLocalStorageRef() || getSessionStorageRef();
}

function getSessionStorageRef() {
  return canUseSessionStorage() ? window.sessionStorage : null;
}

function getLocalStorageRef() {
  return canUseLocalStorage() ? window.localStorage : null;
}

function readJsonFromStorage(storage, key) {
  if (!storage) return null;
  try {
    return JSON.parse(storage.getItem(key) || "null");
  } catch (error) {
    return null;
  }
}

function clearLegacyStorage() {
  const localStorageRef = getLocalStorageRef();
  const sessionStorageRef = getSessionStorageRef();

  if (localStorageRef) {
    localStorageRef.removeItem(LEGACY_TOKEN_STORAGE_KEY);
    localStorageRef.removeItem(LEGACY_USER_STORAGE_KEY);
  }

  if (sessionStorageRef) {
    sessionStorageRef.removeItem(LEGACY_TOKEN_STORAGE_KEY);
    sessionStorageRef.removeItem(LEGACY_USER_STORAGE_KEY);
  }
}

function clearAccountStorage(storage) {
  if (!storage) return;
  storage.removeItem(ACCOUNTS_STORAGE_KEY);
  storage.removeItem(ACTIVE_ACCOUNT_STORAGE_KEY);
}

function hasAssignedRoleData(user) {
  if (!user || typeof user !== "object") return false;
  if (Array.isArray(user.roles) && user.roles.length > 0) return true;
  return Boolean(
    user.role_id ||
      user.active_role_id ||
      user.effective_role_id ||
      user.role_name ||
      user.active_role_name ||
      user.effective_role_name
  );
}

function mergeStoredUser(previousUser, nextUser) {
  if (!previousUser) return nextUser;
  if (!nextUser) return previousUser;

  const nextHasRoleData = hasAssignedRoleData(nextUser);
  const previousHasRoleData = hasAssignedRoleData(previousUser);
  const nextPermissions =
    Array.isArray(nextUser.permissions) && nextUser.permissions.length
      ? nextUser.permissions
      : Array.isArray(previousUser.permissions)
        ? previousUser.permissions
        : [];
  const nextPolicies =
    nextUser.policy_summary &&
    typeof nextUser.policy_summary === "object" &&
    Object.keys(nextUser.policy_summary).length
      ? nextUser.policy_summary
      : previousUser.policy_summary || {};

  return {
    ...previousUser,
    ...nextUser,
    role_id: nextHasRoleData ? nextUser.role_id ?? null : previousUser.role_id ?? null,
    role_name: nextHasRoleData ? nextUser.role_name ?? null : previousUser.role_name ?? null,
    active_role_id: nextHasRoleData ? nextUser.active_role_id ?? null : previousUser.active_role_id ?? null,
    active_role_name: nextHasRoleData ? nextUser.active_role_name ?? null : previousUser.active_role_name ?? null,
    effective_role_id: nextHasRoleData ? nextUser.effective_role_id ?? null : previousUser.effective_role_id ?? null,
    effective_role_name: nextHasRoleData ? nextUser.effective_role_name ?? null : previousUser.effective_role_name ?? null,
    roles:
      nextHasRoleData && Array.isArray(nextUser.roles)
        ? nextUser.roles
        : previousHasRoleData && Array.isArray(previousUser.roles)
          ? previousUser.roles
          : [],
    permissions: nextPermissions,
    policy_summary: nextPolicies,
    isSuperAdmin:
      typeof nextUser.isSuperAdmin === "boolean"
        ? nextUser.isSuperAdmin
        : !!previousUser.isSuperAdmin,
  };
}

function normalizeStoredAccount(account) {
  const user = account?.user;
  const sessionAlias = String(
    account?.session_alias ||
      account?.sessionAlias ||
      account?.refresh_cookie_name ||
      ""
  ).trim();

  if (!user || !sessionAlias) return null;

  const userId = String(user?.id || account?.account_id || "").trim();
  const id = String(
    account?.id || `${userId || user?.email || "account"}:${sessionAlias}`
  ).trim();

  return {
    id,
    account_id: userId || null,
    session_alias: sessionAlias,
    user,
    email: String(user?.email || account?.email || "").trim().toLowerCase(),
    access_token_expires_at: account?.access_token_expires_at || null,
    refresh_token_expires_at: account?.refresh_token_expires_at || null,
  };
}

function normalizeAccounts(rawAccounts) {
  return Array.isArray(rawAccounts)
    ? rawAccounts.map(normalizeStoredAccount).filter(Boolean)
    : [];
}

function readSessionAccounts() {
  return normalizeAccounts(
    readJsonFromStorage(getSessionStorageRef(), ACCOUNTS_STORAGE_KEY)
  );
}

function readLocalAccounts() {
  return normalizeAccounts(
    readJsonFromStorage(getLocalStorageRef(), ACCOUNTS_STORAGE_KEY)
  );
}

function readActiveAccountIdFromStorage(storage) {
  if (!storage) return null;
  return storage.getItem(ACTIVE_ACCOUNT_STORAGE_KEY) || null;
}

function persistNormalizedAccounts(accounts, activeAccountId = null) {
  const preferredStorageRef = getPreferredStorageRef();
  const localStorageRef = getLocalStorageRef();
  const sessionStorageRef = getSessionStorageRef();
  const fallbackStorageRef =
    preferredStorageRef === localStorageRef ? sessionStorageRef : localStorageRef;

  if (!preferredStorageRef) {
    clearLegacyStorage();
    return { accounts: [], activeAccountId: null, activeAccount: null };
  }

  const dedupedMap = new Map();
  accounts
    .map(normalizeStoredAccount)
    .filter(Boolean)
    .forEach((account) => {
      dedupedMap.set(account.id, account);
    });

  const nextAccounts = Array.from(dedupedMap.values());
  const nextActiveAccount =
    nextAccounts.find((account) => account.id === activeAccountId) ||
    nextAccounts[0] ||
    null;
  const nextActiveAccountId = nextActiveAccount?.id || null;

  if (nextAccounts.length) {
    preferredStorageRef.setItem(ACCOUNTS_STORAGE_KEY, JSON.stringify(nextAccounts));
  } else {
    preferredStorageRef.removeItem(ACCOUNTS_STORAGE_KEY);
  }

  if (nextActiveAccountId) {
    preferredStorageRef.setItem(ACTIVE_ACCOUNT_STORAGE_KEY, nextActiveAccountId);
  } else {
    preferredStorageRef.removeItem(ACTIVE_ACCOUNT_STORAGE_KEY);
  }

  clearAccountStorage(fallbackStorageRef);
  clearLegacyStorage();

  return {
    accounts: nextAccounts,
    activeAccountId: nextActiveAccountId,
    activeAccount: nextActiveAccount,
  };
}

function migrateStoredAuthState() {
  const localStorageRef = getLocalStorageRef();
  const sessionStorageRef = getSessionStorageRef();
  const preferredStorageRef = getPreferredStorageRef();

  if (!preferredStorageRef) {
    return { accounts: [], activeAccountId: null, activeAccount: null };
  }

  const localAccounts = readLocalAccounts();
  const sessionAccounts = readSessionAccounts();
  const mergedAccounts = normalizeAccounts([...localAccounts, ...sessionAccounts]);
  const activeAccountId =
    readActiveAccountIdFromStorage(preferredStorageRef) ||
    readActiveAccountIdFromStorage(localStorageRef === preferredStorageRef ? sessionStorageRef : localStorageRef);

  if (!mergedAccounts.length) {
    clearLegacyStorage();
    clearAccountStorage(localStorageRef);
    if (sessionStorageRef !== localStorageRef) {
      clearAccountStorage(sessionStorageRef);
    }
    return { accounts: [], activeAccountId: null, activeAccount: null };
  }

  return persistNormalizedAccounts(mergedAccounts, activeAccountId);
}

export function getStoredAccounts() {
  return migrateStoredAuthState().accounts;
}

export function getStoredActiveAccountId() {
  return migrateStoredAuthState().activeAccountId;
}

export function getActiveStoredAccount(
  accounts = getStoredAccounts(),
  activeAccountId = getStoredActiveAccountId()
) {
  if (!accounts.length) return null;
  return accounts.find((account) => account.id === activeAccountId) || accounts[0];
}

export function persistStoredAccounts(accounts, activeAccountId = null) {
  return persistNormalizedAccounts(accounts, activeAccountId);
}

export function upsertStoredAccount(sessionData) {
  const existingAccounts = getStoredAccounts();
  const account = normalizeStoredAccount(sessionData);
  if (!account) {
    return persistStoredAccounts(getStoredAccounts(), getStoredActiveAccountId());
  }

  const previousAccount = existingAccounts.find(
    (item) =>
      item.id === account.id ||
      (item.account_id === account.account_id && item.session_alias === account.session_alias)
  );
  const mergedAccount = previousAccount
    ? normalizeStoredAccount({
        ...account,
        user: mergeStoredUser(previousAccount.user, account.user),
      })
    : account;
  const existing = existingAccounts.filter((item) => item.id !== mergedAccount.id);
  return persistStoredAccounts([mergedAccount, ...existing], mergedAccount.id);
}

export function setStoredActiveAccount(accountId) {
  return persistStoredAccounts(getStoredAccounts(), accountId);
}

export function updateStoredAccountUser(accountId, nextUser) {
  const nextAccounts = getStoredAccounts().map((account) =>
    account.id === String(accountId)
      ? normalizeStoredAccount({
          ...account,
          user: mergeStoredUser(account.user, nextUser),
        })
      : account
  );

  return persistStoredAccounts(nextAccounts, accountId);
}

export function removeStoredAccount(accountId) {
  const currentActiveId = getStoredActiveAccountId();
  const nextAccounts = getStoredAccounts().filter(
    (account) => account.id !== String(accountId)
  );
  const nextActiveId =
    currentActiveId === String(accountId)
      ? nextAccounts[0]?.id || null
      : currentActiveId;

  return persistStoredAccounts(nextAccounts, nextActiveId);
}

export function clearStoredAccounts() {
  clearLegacyStorage();
  return persistStoredAccounts([], null);
}
